import { unifiedIsoCountries } from "@core/dictionary/global";
import { useEffect, useMemo, useRef, useState } from "react";
import ReactCountryFlag from "react-country-flag";

const ClassificationFilter = ({ options = [], selectedFilters = [], onFilterChange, isCountry = false, placeholder }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [localSelectedFilters, setLocalSelectedFilters] = useState(JSON.parse(JSON.stringify(selectedFilters)));
  const dropdownRef = useRef(null);

  const filteredOptions = useMemo(() => {
    const lowerCaseSearch = searchTerm.trim().toLowerCase();
    return options.filter((option) => unifiedIsoCountries[option]?.toLowerCase()?.includes(lowerCaseSearch) || option?.toLowerCase()?.includes(lowerCaseSearch));
  }, [options, searchTerm]);

  const handleFilterChange = (data) => {
    let updatedFilters = structuredClone(localSelectedFilters);
    if (updatedFilters.includes(data)) {
      updatedFilters = updatedFilters.filter((item) => item !== data);
    } else {
      updatedFilters = [...updatedFilters, data];
    }
    setLocalSelectedFilters(updatedFilters);
  };

  const handleApply = () => {
    onFilterChange(localSelectedFilters);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  useEffect(() => {
    setLocalSelectedFilters(selectedFilters);
  }, [selectedFilters]);

  return (
    <div className="tw-relative" ref={dropdownRef}>
      <div onClick={() => setIsOpen(!isOpen)} className="tw-gap-2 tw-items-center tw-flex">
        {isCountry ? "Country" : "Classified By"}
        <span className="tw-cursor-pointer">
          <svg xmlns="http://www.w3.org/2000/svg" height="20px" viewBox="0 -960 960 960" width="20px" fill="#1c1b1f">
            <path d="M456.18-192Q446-192 439-198.9t-7-17.1v-227L197-729q-9-12-2.74-25.5Q200.51-768 216-768h528q15.49 0 21.74 13.5Q772-741 763-729L528-443v227q0 10.2-6.88 17.1-6.89 6.9-17.06 6.9h-47.88ZM480-498l162-198H317l163 198Zm0 0Z" />
          </svg>
        </span>
      </div>

      {isOpen && (
        <div className="tw-absolute tw-mt-1 tw-bg-white tw-border tw-rounded-2xl tw-shadow-lg tw-z-10 tw-top-[2rem] tw-w-[30rem] tw-right-[-8rem]">
          <div className="tw-p-4 tw-border-b">
            <div className="tw-relative">
              <input className="tw-border tw-border-gray-300 tw-rounded-lg tw-w-full tw-h-[36px] tw-ps-3 tw-bg-gray-25 tw-text-sm tw-outline-none" placeholder={placeholder} value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
            </div>

            {localSelectedFilters.length > 0 && (
              <div className="tw-flex tw-flex-wrap tw-gap-2 tw-mt-2">
                {localSelectedFilters.slice(0, 2).map((filter) => (
                  <span key={filter} className="tw-flex tw-items-center tw-bg-primary-purple-100 tw-py-1 tw-px-2 tw-rounded-full tw-text-primary-purple-500 tw-text-xs tw-font-medium">
                    {isCountry ? (filter === "ZZ" ? "All Countries" : (unifiedIsoCountries[filter] ?? filter)) : filter}
                    <div onClick={() => handleFilterChange(filter)} className="tw-ml-2 tw-cursor-pointer">
                      <svg xmlns="http://www.w3.org/2000/svg" height="20px" viewBox="0 -960 960 960" width="20px" fill="#7143f1">
                        <path d="m291-240-51-51 189-189-189-189 51-51 189 189 189-189 51 51-189 189 189 189-51 51-189-189-189 189Z" />
                      </svg>
                    </div>
                  </span>
                ))}
                {localSelectedFilters.length > 2 && <span className="tw-flex tw-items-center tw-bg-primary-purple-100 tw-py-1 tw-px-2 tw-rounded-full tw-text-primary-purple-500 tw-text-xs tw-font-medium">+{localSelectedFilters.length - 2}</span>}
              </div>
            )}
          </div>

          <div className="tw-max-h-60 tw-overflow-y-auto">
            {filteredOptions.map((option) => (
              <div key={option} onClick={() => handleFilterChange(option)} className="tw-flex tw-items-center tw-gap-1 tw-px-4 tw-py-2 hover:tw-bg-gray-50 tw-cursor-pointer">
                <label className="form-check !tw-mb-0">
                  <input
                    className="form-check-input position-relative mr-0 ml-2"
                    type="checkbox"
                    id={"comp"}
                    checked={localSelectedFilters.includes(option)}
                    onChange={(e) => {
                      e.stopPropagation();
                      handleFilterChange(option);
                    }}
                  />
                  <span className="form-check-label"></span>
                </label>

                <div className="tw-flex tw-items-center">
                  {isCountry && option !== "ZZ" ? <ReactCountryFlag countryCode={option} svg className="tw-mx-2" style={{ width: "1.25em", height: "1.25em" }} /> : null}
                  <span>
                    {isCountry
                      ? option === "ZZ"
                        ? "All Countries"
                        : (unifiedIsoCountries[option] ?? option)
                      : option
                          .split(" ")
                          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                          .join(" ")}
                  </span>
                </div>
              </div>
            ))}
          </div>

          <div className="tw-p-4 tw-border-t">
            <button onClick={handleApply} disabled={localSelectedFilters.length === 0} className={`tw-w-full tw-py-2 tw-px-4 tw-rounded-lg tw-text-sm tw-font-medium ${localSelectedFilters.length === 0 ? "tw-bg-primary-purple-200 tw-text-gray-500 tw-cursor-not-allowed" : "tw-bg-primary-purple-500 tw-text-white"}`}>
              Apply
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClassificationFilter;
